import { LogManager, Navigation, UrlUtil } from "@mcleod/core";
import {
    ClickEvent, ContextMenuLabelProps, DataHeader, DataSource, DataSourceAction, DataSourceExecutionEvent,
    DataSourceMode
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutViewBilling } from "./autogen/AutogenLayoutViewBilling";
import { MovementTabView } from "@mcleod/dispatch/src/MovementTabView";
import { Movement } from "@mcleod/dispatch";
import { Billing } from "./Billing";

const log = LogManager.getLogger("lme.ar.Billing");

interface ViewBillingNavigateProps {
    key?: string;
    billingId?: string;
    newTab?: boolean;
    additionalProps?: {};
    styleProps?: {};
}

const layoutURL = "lme/ar/ViewBilling";
export class ViewBilling extends AutogenLayoutViewBilling {
    private _activeLayout: Billing | MovementTabView;
    private mode: DataSourceMode;

    /**
     * Navigates to the ViewBilling layout
     * @param props Navigation properties
     * @param props.key The billing/order key
     * @param props.billingId The billing ID
     * @param props.newTab Whether to open in a new tab
     * @param props.additionalProps Additional properties to pass to the layout
     * @param props.styleProps Style properties for new tab/window
     */
    public static navigateTo(props: ViewBillingNavigateProps): void {
        Navigation.navigateTo(
            layoutURL + `?mode=${props.key ? DataSourceMode.UPDATE : DataSourceMode.ADD}`
            + (props.key ? `&key=${props.key}` : "")
            + (props.billingId ? `&billingId=${props.billingId}` : ""),
            { newTab: props.newTab, ...props.styleProps },
            { additionalProps: props.additionalProps }
        );
    }    
    
    override onLoad() {
        this.activeLayout = this.getActiveLayout();
        this.getAdditionalProps();
        this.setupLayouts();
        this.shareNavigateOnClose();
    }

    /**
     * This method ensures that, when navigation instructions have been provided to one of the layouts (orders or
     * movements) and not the other, that those navigation instructions are shared with the other layout.  The result of
     * this is that closing the CrudDecorator from either layout will results in the same behavior (unless different
     * navigation instructions have already been assigned to each layout).
     *
     * Note that in a standard CrudDecorator scenario, only one of the two layouts will have been assigned navigation
     * instructions (whichever layout is the one that is first visible to the user).  Those navigation instructions will
     * be to slide out the CrudDecorator to remove it from the page.  So in most cases we expect those standard slide
     * out instructions to simply be applied the second layout.
     */
    private shareNavigateOnClose() {
        this.addLayoutLoadListener(() => {
            const orderLayoutHasNavigateOnClose = this.layoutBilling.dataHeader.navigateOnClose != null;
            const movementLayoutHasNavigateOnClose = this.layoutMovementTabView.dataHeader.navigateOnClose != null;
            if (orderLayoutHasNavigateOnClose && !movementLayoutHasNavigateOnClose)
                this.layoutMovementTabView.dataHeader.navigateOnClose = this.layoutBilling.dataHeader.navigateOnClose;
            if (movementLayoutHasNavigateOnClose && !orderLayoutHasNavigateOnClose)
                this.layoutBilling.dataHeader.navigateOnClose = this.layoutMovementTabView.dataHeader.navigateOnClose;
        });
    }

    override getTitleForMode(mode: DataSourceMode): string {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        return urlProps ? urlProps.orderId ? `Movement - ${urlProps.key}` : `Invoice - ${urlProps.key}` : "Add Invoice";
    }

    override get mainDataSource(): DataSource {
        return this.getActiveLayout().mainDataSource;
    }

    override setAllDataSourcesToMode(mode: DataSourceMode): void {
        this.getActiveLayout().setAllDataSourcesToMode(mode);
    }

    override get dataHeader(): DataHeader {
        const dh = this.getActiveLayout().dataHeader;
        if (this.getActiveLayout() == this.layoutBilling) {
            if (dh.layout == null) dh.layout = this.layoutBilling;
            this.copyDataHeaderAttributes(dh, this.layoutMovementTabView.dataHeader);
        } else {
            if (dh.layout == null) dh.layout = this.layoutMovementTabView;
            this.copyDataHeaderAttributes(dh, this.layoutBilling.dataHeader);
        }
        this.updateDataHeader(dh);
        return dh;
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        if (dataHeader.layout != this.activeLayout) {
            dataHeader.layout = this.activeLayout;
        }
        if (this.displayingFromToolbox) {
            dataHeader.showSave = false;
            dataHeader.showSaveAndClose = true;
        }
        this.setDataSourceMode();
    }

    copyDataHeaderAttributes(dh1: DataHeader, dh2: DataHeader) {
        dh2.showClose = dh1.showClose;
        dh2.showSaveAndClose = dh1.showSaveAndClose;
        dh2.onExecute = dh1.onExecute;
    }

    updateDataHeader(dataHeader: DataHeader) {
        dataHeader["labelTitle"].visible = false;
        dataHeader.marginBottom = 0;
        if (dataHeader.dataSource == null) {
            dataHeader.dataSource = this.getActiveLayout().mainDataSource;
        }
    }

    setupLayouts() {
        this.setupBillingLayout();
        this.setupMovementLayout();
    }

    setupBillingLayout() {
        this.layoutBilling.displayingFromToolbox = this.displayingFromToolbox;
        this.layoutBilling.panelMovementTab.addClickListener(async (event: ClickEvent) => {
            this.handleTabChange(true);
            if (this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.sourceMovementOnDisplay();
            }
            if (this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.sourceMovementOnDisplay();
            }
        });
        this.layoutBilling.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => this.doAfterExecuteEvent(event, this.layoutMovementTabView));

        this.layoutBilling.carrierAssignMentChanged = () => {
            if (this.layoutMovementTabView.activeRow) {
                this.layoutMovementTabView.layoutMovement.search({ id: this.layoutMovementTabView.activeRow.get("id") });
            }
        }
    }

    setupMovementLayout() {
        this.layoutMovementTabView.getTitleForMode = () => this.getTitleForMode(this.mode);
        this.layoutMovementTabView.layoutMovement.labelOrderView.text = "Invoice Processing View";
        this.layoutMovementTabView.handleOrderTabChange = async () => {
            this.handleTabChange(false)
            await this.layoutBilling.layoutOverview.sourceOrderOnDisplay();
        };
        this.layoutMovementTabView.movementTabOnChange = () => this.parent.replace(this.parent.components[0], this.dataHeader);
        this.layoutMovementTabView.movementRefreshed = () => {
            if (this.layoutBilling.activeRow) {
                this.layoutBilling.search({ id: this.layoutBilling.activeRow.get("id") });
            }
        };
        this.layoutMovementTabView.activeMovementChanged = (movement: Movement) => {
            // Set up the doAfterExecuteEvent first
            if (movement["doAfterExecuteEvent"] == null) {
                movement["doAfterExecuteEvent"] = (event: DataSourceExecutionEvent) => this.doAfterExecuteEvent(event, this.layoutBilling);
                movement.mainDataSource.addAfterExecutionListener(movement["doAfterExecuteEvent"]);
            }
        }
    }

    setDataSourceMode() {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (this.displayingFromToolbox || urlProps == null || urlProps?.mode == "add" || this.dataHeader.parent["mode"] == DataSourceMode.ADD) {
            this.mode = DataSourceMode.ADD;
            this.layoutBilling.panelMovementTab.visible = false;
            this.layoutBilling.panelOrderTab.borderRightWidth = 0;
            this.layoutBilling.labelOrderView.text = "Add Invoice";
            this.layoutBilling.labelStatusBadge.visible = false;
        } else {
            this.mode = DataSourceMode.UPDATE;
        }
    }

    getAdditionalProps() {
        const additionalProps = this["additionalProps"];
        if (additionalProps) {
            for (const prop in additionalProps) {
                if (this.activeLayout instanceof MovementTabView) {
                    this.activeLayout.layoutMovement[prop] = additionalProps[prop];
                } else {
                    this.activeLayout[prop] = additionalProps[prop];
                }
            }
        }
    }    
    
    getActiveLayout() {
        let activeLayout: Billing | MovementTabView = this.activeLayout;
        if (this.activeLayout == null) {
            activeLayout = this.layoutBilling;
            activeLayout.hideTitle = false;
            if (!this.displayingFromToolbox) {
                const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
                if (urlProps?.orderId) {
                    activeLayout = this.layoutMovementTabView;
                }
            }
        }
        return activeLayout;
    }    get activeLayout(): Billing | MovementTabView {
        return this._activeLayout;
    }

    set activeLayout(layout: Billing | MovementTabView) {
        this._activeLayout = layout;
        this._activeLayout.visible = true;
    }

    doAfterExecuteEvent(event: DataSourceExecutionEvent, layoutToRefresh: Billing | MovementTabView) {
        if (event.getAction() == DataSourceAction.UPDATE) {
            if (layoutToRefresh instanceof Billing) {
                // Post moves that have changed data
                const changedMovement = this.layoutMovementTabView.getChangedMovement();
                if (changedMovement != null) {
                    changedMovement.mainDataSource.post();
                    return;
                }
            }
            if (layoutToRefresh.activeRow) {
                if (layoutToRefresh instanceof MovementTabView) {
                    layoutToRefresh.layoutMovementTabs.currentTabId = null;
                }
                layoutToRefresh.search({ id: layoutToRefresh.activeRow.get("id") });
            }
        }
    }

    public static addNavigationContextMenuItems(itemProps: Partial<ContextMenuLabelProps>[], billingId: string): Partial<ContextMenuLabelProps>[] {
        if (itemProps != null)  {
            const navigationItems = ViewBilling.createNavigationContextMenuItems(billingId);
            if (itemProps.length > 0 && navigationItems.length > 0) {
                const allVisibleItems = itemProps.filter(item => item.visible);
                const lastVisibleItem = allVisibleItems[allVisibleItems.length - 1];
                if (lastVisibleItem) lastVisibleItem.separatorAfter = true;
                itemProps.push(...navigationItems);
            }
        }
        return itemProps;
    }

    public static createNavigationContextMenuItems(billingId: string): Partial<ContextMenuLabelProps>[] {
        if (billingId == null)  return [];
        return [
            {
                text: "Open in New Tab",
                onClick: () => ViewBilling.navigateTo({
                    key: billingId,
                    newTab: true
                })
            },
            {
                text: "Open in New Window",
                onClick: () => ViewBilling.navigateTo({
                    key: billingId,
                    newTab: true,
                    styleProps: { windowDecorators: false }
                })
            }
        ]
    }

    async handleTabChange(isOrder: boolean): Promise<void> {
        if (!this.activeLayout || !this.activeLayout.activeRow) return;

        let cachedBillingId: string | undefined;
        let cachedOrderId: string | undefined;

        if(this.layoutBilling?.sourceBilling?.activeRow) {
            cachedOrderId = this.layoutBilling?.sourceBilling?.activeRow?.get?.("order_id");
            cachedBillingId = this.layoutBilling?.sourceBilling?.activeRow?.get?.("id");
        } else if (this.layoutMovementTabView?.activeRow) {
            cachedOrderId = this.layoutMovementTabView?.activeRow?.get?.("orders.id");
        }

        // Handle unsaved changes
        if (this.activeLayout.mainDataSource?.hasChanged(true)) {
            try {
                const saveChanges = await CommonDialogs.showYesNo(
                    "Save your changes on this tab before switching?",
                    "Save Changes"
                );

                if (saveChanges === undefined) return;
                if (saveChanges) {
                    await this.activeLayout.mainDataSource.post();
                }
            } catch (error) {
                log.debug("Error while saving changes:", error);
                return;
            }
        }

        const oldDataHeader = this.dataHeader;
        let id: string | undefined;

        try {
            if (isOrder) {
                this.switchToOrderTab();
                id = await this.resolveMovementId(cachedOrderId);
            } else {
                this.switchToBillingTab();
                id = cachedBillingId;
            }

            await this.activateRowIfMissing(id);
        } catch (error) {
            log.debug("Tab switch failed:", error);
            return;
        }

        this.parent?.replace?.(oldDataHeader, this.dataHeader);
    }

    private switchToOrderTab(): void {
        if (!this.layoutBilling || !this.layoutMovementTabView) return;
        this.layoutBilling.visible = false;
        this.activeLayout = this.layoutMovementTabView;
        this.layoutMovementTabView.visible = true;
    }

    private async resolveMovementId(fallbackOrderId?: string): Promise<string | undefined> {
        if (!this.layoutMovementTabView || !this.layoutBilling) return undefined;

        if (!this.layoutMovementTabView.activeRow && fallbackOrderId) {
            await this.layoutBilling.sourceOrders?.search?.({ id: fallbackOrderId });
        }

        return this.layoutBilling.sourceOrders?.activeRow?.get?.("curr_movement_id");
    }

    private switchToBillingTab(): void {
        if (!this.layoutBilling || !this.layoutMovementTabView) return;
        this.layoutMovementTabView.visible = false;
        this.activeLayout = this.layoutBilling;
        this.layoutBilling.visible = true;
    }

    private async activateRowIfMissing(id?: string): Promise<void> {
        if (!id || this.activeLayout?.activeRow) return;
        try {
            await this.activeLayout.search?.({ id });

            if (this.activeLayout.mainDataSource) {
                this.activeLayout.mainDataSource.mode = this.mode;
            }
        } catch (error) {
            log.debug("Error activating row:", error);
        }
    }
}
